import { type FeedbackRowWithAsset } from '../contacts/types.ts'

export type FeedbackRequestEligibilityReason =
  | 'ok'
  | 'alreadyRequested'
  | 'contactDisabled'
  | 'cooldown'

export type FeedbackRequestEligibility = {
  canSend: boolean
  reason: FeedbackRequestEligibilityReason
  nextAllowedAt?: Date
}

type FeedbackEligibilityInput = {
  feedbackRows: FeedbackRowWithAsset[]
  contactDisabledAt?: string | null
  contactDisabledReason?: string | null
  now?: Date
}

export const GENERAL_FEEDBACK_COOLDOWN_MONTHS = 4

function isContactDisabled(
  contactDisabledAt: string | null | undefined,
  contactDisabledReason: string | null | undefined
): boolean {
  if (contactDisabledAt === null || contactDisabledAt === undefined) {
    return false
  }

  if (contactDisabledReason === 'RESERVED') {
    return false
  }

  return true
}

function isPendingGeneralFeedback(row: FeedbackRowWithAsset): boolean {
  if (row.completed_at !== null) {
    return false
  }

  if (row.declined_at !== null && row.declined_at !== undefined) {
    return false
  }

  return true
}

export function getFeedbackRequestEligibility({
  feedbackRows,
  contactDisabledAt,
  contactDisabledReason,
  now = new Date(),
}: FeedbackEligibilityInput): FeedbackRequestEligibility {
  if (isContactDisabled(contactDisabledAt, contactDisabledReason)) {
    return { canSend: false, reason: 'contactDisabled' }
  }

  const hasPendingGeneralFeedback = feedbackRows.some(isPendingGeneralFeedback)

  if (hasPendingGeneralFeedback) {
    return { canSend: false, reason: 'alreadyRequested' }
  }

  const latestCompleted = feedbackRows
    .filter((row) => !!row.completed_at)
    .sort((rowA, rowB) => new Date(rowB.completed_at!).getTime() - new Date(rowA.completed_at!).getTime())[0]

  if (latestCompleted?.completed_at) {
    const nextAllowedAt = new Date(latestCompleted.completed_at)
    nextAllowedAt.setMonth(nextAllowedAt.getMonth() + GENERAL_FEEDBACK_COOLDOWN_MONTHS)

    if (now < nextAllowedAt) {
      return { canSend: false, reason: 'cooldown', nextAllowedAt }
    }
  }

  return { canSend: true, reason: 'ok' }
}
